import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import MetricCard from './components/MetricCard';
import ExecutiveChart from './components/ExecutiveChart';
import CostBreakdownChart from './components/CostBreakdownChart';
import BenchmarkComparison from './components/BenchmarkComparison';
import SustainabilityScorecard from './components/SustainabilityScorecard';
import InitiativeProgress from './components/InitiativeProgress';
import TimePeriodSelector from './components/TimePeriodSelector';

const ExecutiveSummaryDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('current-quarter');
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdated(new Date());
    }, 3600000); // Update every hour

    return () => clearInterval(interval);
  }, []);

  // Mock data for executive metrics
  const executiveMetrics = [
    {
      title: "Operational Efficiency",
      value: "94.2",
      unit: "%",
      trend: "up",
      trendValue: "+2.1%",
      benchmark: "87.5%",
      icon: "Zap",
      color: "success",
      description: "Overall equipment effectiveness across all facilities"
    },
    {
      title: "Cost Savings YTD",
      value: "2.8",
      unit: "M",
      trend: "up",
      trendValue: "+15.3%",
      benchmark: "$2.1M",
      icon: "DollarSign",
      color: "primary",
      description: "Total cost reduction achieved through optimization"
    },
    {
      title: "Sustainability Score",
      value: "87",
      unit: "/100",
      trend: "up",
      trendValue: "+5.2",
      benchmark: "82/100",
      icon: "Leaf",
      color: "success",
      description: "ESG compliance and environmental impact rating"
    },
    {
      title: "Safety Performance",
      value: "99.1",
      unit: "%",
      trend: "up",
      trendValue: "+0.8%",
      benchmark: "96.3%",
      icon: "Shield",
      color: "warning",
      description: "Incident-free operations and safety compliance"
    }
  ];

  // Mock data for revenue impact chart
  const revenueImpactData = [
    { name: 'Jan', revenue: 12500000, costs: 8200000, profit: 4300000 },
    { name: 'Feb', revenue: 13200000, costs: 8500000, profit: 4700000 },
    { name: 'Mar', revenue: 14100000, costs: 8800000, profit: 5300000 },
    { name: 'Apr', revenue: 13800000, costs: 8600000, profit: 5200000 },
    { name: 'May', revenue: 15200000, costs: 9100000, profit: 6100000 },
    { name: 'Jun', revenue: 14900000, costs: 8900000, profit: 6000000 },
    { name: 'Jul', revenue: 16100000, costs: 9400000, profit: 6700000 },
    { name: 'Aug', revenue: 15800000, costs: 9200000, profit: 6600000 },
    { name: 'Sep', revenue: 17200000, costs: 9800000, profit: 7400000 },
    { name: 'Oct', revenue: 16800000, costs: 9600000, profit: 7200000 },
    { name: 'Nov', revenue: 18100000, costs: 10200000, profit: 7900000 },
    { name: 'Dec', revenue: 17500000, costs: 9900000, profit: 7600000 }
  ];

  // Mock data for productivity trends
  const productivityData = [
    { name: 'Q1 2023', efficiency: 82, utilization: 78, output: 85 },
    { name: 'Q2 2023', efficiency: 85, utilization: 81, output: 88 },
    { name: 'Q3 2023', efficiency: 87, utilization: 84, output: 90 },
    { name: 'Q4 2023', efficiency: 89, utilization: 86, output: 92 },
    { name: 'Q1 2024', efficiency: 91, utilization: 88, output: 94 },
    { name: 'Q2 2024', efficiency: 93, utilization: 90, output: 96 },
    { name: 'Q3 2024', efficiency: 94, utilization: 91, output: 97 },
    { name: 'Q4 2024', efficiency: 94.2, utilization: 92, output: 98 }
  ];

  // Mock data for ROI analysis
  const roiData = [
    { name: 'Fleet Optimization', investment: 2500000, returns: 4200000, roi: 68 },
    { name: 'Warehouse Automation', investment: 3200000, returns: 5800000, roi: 81 },
    { name: 'Energy Efficiency', investment: 1800000, returns: 2900000, roi: 61 },
    { name: 'Predictive Maintenance', investment: 1500000, returns: 3100000, roi: 107 },
    { name: 'Supply Chain Optimization', investment: 2800000, returns: 4900000, roi: 75 }
  ];

  // Mock data for cost breakdown
  const costBreakdownData = [
    { name: 'Fleet Operations', value: 3200000 },
    { name: 'Warehouse Operations', value: 2800000 },
    { name: 'Energy & Utilities', value: 1500000 },
    { name: 'Maintenance', value: 1200000 },
    { name: 'Technology & Systems', value: 800000 },
    { name: 'Other', value: 500000 }
  ];

  // Mock data for benchmark comparison
  const benchmarkData = [
    {
      metric: 'Fleet Efficiency',
      value: '94.2%',
      benchmark: '87.5%',
      performance: 94,
      insight: 'Exceeding industry average by 6.7 percentage points through route optimization'
    },
    {
      metric: 'Warehouse Productivity',
      value: '98.1%',
      benchmark: '92.3%',
      performance: 98,
      insight: 'Leading performance driven by automation and process improvements'
    },
    {
      metric: 'Energy Efficiency',
      value: '87.4%',
      benchmark: '81.2%',
      performance: 87,
      insight: 'Strong performance with renewable energy initiatives showing impact'
    },
    {
      metric: 'Safety Compliance',
      value: '99.1%',
      benchmark: '96.3%',
      performance: 99,
      insight: 'Industry-leading safety record with comprehensive training programs'
    }
  ];

  // Mock data for sustainability scorecard
  const sustainabilityData = [
    {
      category: 'Carbon Footprint',
      score: 85,
      trend: 'up',
      change: '+3.2',
      description: 'CO2 emissions reduction',
      target: '90',
      icon: 'Leaf'
    },
    {
      category: 'Energy Efficiency',
      score: 92,
      trend: 'up',
      change: '+1.8',
      description: 'Renewable energy usage',
      target: '95',
      icon: 'Zap'
    },
    {
      category: 'Waste Management',
      score: 78,
      trend: 'stable',
      change: '0.0',
      description: 'Waste reduction & recycling',
      target: '85',
      icon: 'Recycle'
    },
    {
      category: 'Water Conservation',
      score: 88,
      trend: 'up',
      change: '+2.5',
      description: 'Water usage optimization',
      target: '90',
      icon: 'Droplets'
    },
    {
      category: 'Supply Chain',
      score: 82,
      trend: 'up',
      change: '+4.1',
      description: 'Sustainable sourcing',
      target: '88',
      icon: 'Truck'
    }
  ];

  // Mock data for key initiatives
  const initiativesData = [
    {
      name: 'Fleet Electrification Program',
      description: 'Transition 40% of fleet to electric vehicles by Q2 2025',
      status: 'on-track',
      progress: 65,
      dueDate: 'Jun 2025',
      budget: 5200000,
      teamSize: 12,
      expectedROI: '180%',
      milestones: [
        { name: 'Infrastructure Planning', completed: true, date: 'Jan 2024' },
        { name: 'Pilot Program Launch', completed: true, date: 'Mar 2024' },
        { name: 'Phase 1 Deployment', completed: false, date: 'Dec 2024' },
        { name: 'Full Implementation', completed: false, date: 'Jun 2025' }
      ]
    },
    {
      name: 'Warehouse Automation Expansion',
      description: 'Deploy AI-powered robotics across all distribution centers',
      status: 'at-risk',
      progress: 45,
      dueDate: 'Mar 2025',
      budget: 8500000,
      teamSize: 18,
      expectedROI: '220%',
      milestones: [
        { name: 'Technology Selection', completed: true, date: 'Feb 2024' },
        { name: 'Pilot Installation', completed: true, date: 'May 2024' },
        { name: 'System Integration', completed: false, date: 'Nov 2024' },
        { name: 'Full Rollout', completed: false, date: 'Mar 2025' }
      ]
    },
    {
      name: 'Carbon Neutral Operations',
      description: 'Achieve net-zero carbon emissions across all facilities',
      status: 'on-track',
      progress: 78,
      dueDate: 'Dec 2025',
      budget: 12000000,
      teamSize: 25,
      expectedROI: '150%',
      milestones: [
        { name: 'Baseline Assessment', completed: true, date: 'Jan 2024' },
        { name: 'Renewable Energy Setup', completed: true, date: 'Jun 2024' },
        { name: 'Process Optimization', completed: true, date: 'Sep 2024' },
        { name: 'Carbon Offset Program', completed: false, date: 'Dec 2025' }
      ]
    }
  ];

  const formatLastUpdated = () => {
    return lastUpdated?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Executive Summary Dashboard - AutoFleet Analytics</title>
        <meta name="description" content="C-level executive dashboard with strategic KPIs, cost savings visualization, and performance metrics for data-driven decision making." />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Executive Summary</h1>
              <p className="text-muted-foreground">
                Strategic performance overview and key business metrics
              </p>
              <div className="text-sm text-muted-foreground mt-1">
                Last updated: {formatLastUpdated()}
              </div>
            </div>
            <div className="mt-4 lg:mt-0">
              <TimePeriodSelector
                selectedPeriod={selectedPeriod}
                onPeriodChange={setSelectedPeriod}
                selectedFacility={selectedFacility}
                onFacilityChange={setSelectedFacility}
              />
            </div>
          </div>

          {/* Executive Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {executiveMetrics?.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>

          {/* Main Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            <div className="lg:col-span-12">
              <ExecutiveChart
                title="Revenue Impact Analysis"
                data={revenueImpactData}
                type="area"
                height={350}
                dataKeys={['revenue', 'costs', 'profit']}
                colors={['#2563EB', '#EF4444', '#10B981']}
              />
            </div>
          </div>

          {/* Secondary Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <CostBreakdownChart
              title="Operational Cost Breakdown"
              data={costBreakdownData}
            />
            
            <BenchmarkComparison
              title="Industry Benchmark Comparison"
              data={benchmarkData}
            />
            
            <SustainabilityScorecard
              title="Sustainability Scorecard"
              data={sustainabilityData}
              overallScore={85}
            />
          </div>

          {/* Productivity and ROI Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <ExecutiveChart
              title="Productivity Trends"
              data={productivityData}
              type="line"
              height={300}
              dataKeys={['efficiency', 'utilization', 'output']}
              colors={['#2563EB', '#10B981', '#F59E0B']}
            />
            
            <ExecutiveChart
              title="ROI Analysis by Initiative"
              data={roiData}
              type="bar"
              height={300}
              dataKeys={['roi']}
              colors={['#2563EB']}
            />
          </div>

          {/* Key Initiatives Progress */}
          <div className="mb-8">
            <InitiativeProgress
              title="Strategic Initiative Progress"
              initiatives={initiativesData}
            />
          </div>

          {/* Print-friendly Summary */}
          <div className="print:block hidden bg-card border border-border rounded-lg p-6 card-shadow">
            <h2 className="text-xl font-bold text-foreground mb-4">Executive Summary Report</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Key Performance Indicators</h3>
                <ul className="space-y-1 text-sm">
                  {executiveMetrics?.map((metric, index) => (
                    <li key={index} className="flex justify-between">
                      <span>{metric?.title}:</span>
                      <span className="font-medium">{metric?.value}{metric?.unit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Initiative Status</h3>
                <ul className="space-y-1 text-sm">
                  {initiativesData?.map((initiative, index) => (
                    <li key={index} className="flex justify-between">
                      <span>{initiative?.name}:</span>
                      <span className="font-medium">{initiative?.progress}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExecutiveSummaryDashboard;